import { useState } from "react";
import { Button, FormField, Modal, NumberInput } from "@a2z/ui";
import { useTranslate } from "@/lib/i18n";
import { channelLabel } from "@/lib/invoice/place";
import type { AggregatorChoice, Invoice } from "@/lib/api/types";
import {
  UnsavedChangesPrompt,
  useDiscardGuard,
} from "@/shared/UnsavedChangesPrompt";
import { roundMoney } from "../lib/openInvoicePayload";
import {
  AggregatorChips,
  PLATFORM_ORDER_REF_MAX,
  PlatformOrderFields,
} from "./AggregatorChips";

/** The two channels a bill at no table can be — the only two this edits. */
export type OffPremiseChannel = "TAKEAWAY" | "DELIVERY";

/**
 * The `editCharges` payload, exactly as the command contract (C1) states it:
 * all three REQUIRED, `null` meaning the store's default fee and "direct".
 */
export type EditChargesPayload = {
  channel: OffPremiseChannel;
  deliveryFee: number | null;
  aggregatorId: string | null;
  /** T13 — REQUIRED and nullable like its neighbours; null without a platform. */
  platformOrderRef: string | null;
};

/**
 * Whether any plate has REACHED THE KITCHEN (T6, round 2) — fired, cooking,
 * ready or served. A chit for such a plate is already on the pass carrying
 * the old channel and platform, so the dialog warns to reprint. `PENDING`
 * (a guest's request) and `DRAFT` (the basket) have no chit yet; a VOIDED
 * plate's chit is void anyway.
 */
export function hasPlatesInKitchen(
  items: readonly { fulfillmentState: string }[],
): boolean {
  return items.some(
    (item) =>
      item.fulfillmentState === "QUEUED" ||
      item.fulfillmentState === "PREPARING" ||
      item.fulfillmentState === "READY" ||
      item.fulfillmentState === "SERVED",
  );
}

/**
 * CORRECTING WHAT AN OFF-PREMISE BILL CHARGES (T6, 2026-10-06).
 *
 * `OffPremiseDialog` asks three money-bearing questions once, at open — which
 * channel, what fee, which platform — and until this existed a mis-tap on any
 * of them was wrong for the life of the bill: a Talabat order rung up as direct
 * paid no commission on the margin report, a takeaway opened as a delivery
 * carried a fee the customer never owed, and the one remedy was cancelling a
 * bill that had already burnt a daily sequence. `CustomerDialog` deliberately
 * refused these fields ("the fee is priced into the bill's stored totals"),
 * and that refusal stands: this is a DIFFERENT door, on the cashier's rung
 * (`RESTAURANT_INVOICE_CLOSE`), because it reprices the bill.
 *
 * OPEN ONLY. The cheque a customer is holding must not change under them, so
 * the server refuses once CLOSED (`RESTAURANT_INVOICE_NOT_OPEN`) and the page
 * gates the control identically.
 *
 * THE CHANNEL MAY SWITCH TAKEAWAY ↔ DELIVERY, never to or from DINE_IN: a seated
 * bill has a table and a takeaway has none, and that is not a correction, it is
 * a different bill. The server re-splits the fee for the target channel (a
 * TAKEAWAY zeroes it), re-snapshots the service charge for it, and drops the
 * address when a bill leaves DELIVERY — so the dialog says, in its hint, that
 * the figures move.
 *
 * THE FEE FIELD IS PRE-FILLED WITH THE BILL'S OWN FEE, unlike the open dialog,
 * for `CustomerDialog`'s reason: the point of a correction is to see what is on
 * the bill and change the one wrong thing, and an empty box here would turn
 * "correct the fee" into "re-type the fee". It is shown as the cashier typed it
 * at open — gross where the store prices tax-inclusive, net otherwise — because
 * that is the figure the server takes back. BLANK still means the store's
 * default (`deliveryFee: null`), which is a different answer from 0.
 *
 * The platform list includes the bill's CURRENT platform even if it has since
 * been retired, so saving a fee correction never silently moves the bill to
 * direct.
 */
export function ChargesDialog({
  invoice,
  aggregators,
  defaultDeliveryFee,
  isSubmitting,
  onCancel,
  onConfirm,
}: {
  invoice: Pick<
    Invoice,
    | "channel"
    | "deliveryFee"
    | "deliveryFeeVat"
    | "aggregatorId"
    | "pricesIncludeTax"
    | "platformOrderRef"
  > & { items: readonly { fulfillmentState: string }[] };
  /** The store's platforms — every row; this dialog picks the active ones. */
  aggregators: AggregatorChoice[];
  /** The store's own default, shown as the fee field's placeholder. */
  defaultDeliveryFee: number | null;
  isSubmitting: boolean;
  onCancel: () => void;
  onConfirm: (payload: EditChargesPayload) => void | Promise<void>;
}) {
  const t = useTranslate();
  // A dine-in bill never reaches this dialog (the page gates it); the type
  // still has to be narrowed, and TAKEAWAY is the harmless reading.
  const initialChannel: OffPremiseChannel =
    invoice.channel === "DELIVERY" ? "DELIVERY" : "TAKEAWAY";
  const initialFee =
    invoice.channel === "DELIVERY"
      ? String(
          roundMoney(
            invoice.pricesIncludeTax
              ? invoice.deliveryFee + invoice.deliveryFeeVat
              : invoice.deliveryFee,
          ),
        )
      : "";
  const [channel, setChannel] = useState<OffPremiseChannel>(initialChannel);
  const [deliveryFeeRaw, setFee] = useState(initialFee);
  const [aggregatorId, setAggregator] = useState<string | null>(
    invoice.aggregatorId,
  );
  const initialRef = invoice.platformOrderRef ?? "";
  const [platformOrderRef, setPlatformOrderRef] = useState(initialRef);
  const onPlatform = aggregatorId !== null;
  /*
   * T14 ON THE CORRECTION PATH (reviewer, 2026-10-07). The fee box is
   * pre-filled with the bill's CURRENT fee so a cashier can read it, and the
   * server treats any number it receives as "typed — wins". So a direct
   * delivery opened on the store default, then realised to be a platform
   * order, would have carried that default fee onto the platform bill — the
   * exact booking of revenue the restaurant never touches that T14 exists to
   * stop. Moving from direct to a platform therefore clears a fee the cashier
   * has NOT touched (it still equals the pre-fill), so the payload sends
   * `null` and the server's platform default (0) applies; a fee the cashier
   * typed is theirs and stays.
   */
  const pickPlatform = (next: string | null) => {
    if (next !== null && aggregatorId === null && deliveryFeeRaw === initialFee) {
      setFee("");
    }
    setAggregator(next);
  };
  // T6 round 2 — a chit already on the pass carries the OLD details.
  const platesInKitchen = hasPlatesInKitchen(invoice.items);

  const isDelivery = channel === "DELIVERY";
  const feeText = deliveryFeeRaw.trim();
  const fee = Number.parseFloat(feeText);
  // Blank is a real answer (the default); anything typed must be money.
  const feeInvalid =
    isDelivery && feeText !== "" && !(Number.isFinite(fee) && fee >= 0);

  const choices = aggregators.filter(
    (row) => row.isActive || row.id === invoice.aggregatorId,
  );

  const guard = useDiscardGuard(
    channel !== initialChannel ||
      (isDelivery && deliveryFeeRaw !== initialFee) ||
      aggregatorId !== invoice.aggregatorId ||
      (onPlatform && platformOrderRef.trim() !== initialRef),
    onCancel,
  );

  const payload = (): EditChargesPayload => ({
    channel,
    // On a TAKEAWAY the fee is nothing to say — the server zeroes it — so it
    // goes as `null`, never as the stale number from when this was a delivery.
    deliveryFee: isDelivery && feeText !== "" ? roundMoney(fee) : null,
    aggregatorId,
    // T13 — trimmed, blank → null, and null without a platform: the server
    // drops it then anyway, and the wire should say what the screen shows.
    platformOrderRef: onPlatform
      ? platformOrderRef.trim().slice(0, PLATFORM_ORDER_REF_MAX) || null
      : null,
  });

  return (
    <>
      <Modal
        isOpen
        onClose={guard.requestClose}
        size="sm"
        title={t((m) => m.pos.chargesEditTitle)}
        closeLabel={t((m) => m.common.close)}
        data-testid="charges-dialog"
        footerActions={
          <>
            <Button
              variant="outline"
              size="touch"
              disabled={isSubmitting}
              onClick={guard.requestClose}
              data-testid="charges-cancel"
            >
              {t((m) => m.common.cancel)}
            </Button>
            <Button
              variant="primary"
              size="touch"
              className="font-extrabold"
              disabled={isSubmitting || feeInvalid}
              isLoading={isSubmitting}
              data-testid="charges-save"
              onClick={() => void onConfirm(payload())}
            >
              {t((m) => m.common.save)}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {/* WHERE THE FOOD GOES — the two off-premise channels only. Chips,
              like the platform picker below: two options on a touch screen. */}
          <div>
            <span className="mb-1.5 block text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.06em] text-[color:var(--text-disabled)]">
              {t((m) => m.pos.chargesChannelLabel)}
            </span>
            <div
              className="flex flex-wrap gap-1.5"
              role="group"
              aria-label={t((m) => m.pos.chargesChannelLabel)}
            >
              {(["TAKEAWAY", "DELIVERY"] as const).map((option) => {
                const isCurrent = option === channel;
                return (
                  <button
                    key={option}
                    type="button"
                    aria-pressed={isCurrent}
                    disabled={isSubmitting}
                    data-testid={`charges-channel-${option}`}
                    onClick={() => setChannel(option)}
                    className={[
                      "rounded-[var(--radius-control)] border-[length:var(--border-panel-width,1px)] px-2 py-[5px] text-[length:var(--font-size-2xs)] font-extrabold transition-colors",
                      isCurrent
                        ? "border-[color:var(--accent)] bg-[color:var(--accent)] text-[color:var(--accent-contrast)]"
                        : "border-[color:var(--border-strong)] text-[color:var(--text-muted)] hover:text-[color:var(--text)]",
                    ].join(" ")}
                  >
                    {channelLabel(option, t)}
                  </button>
                );
              })}
            </div>
          </div>

          {/* The fee, DELIVERY only — the same field, the same placeholder and
              the same two helper sentences as the open dialog, so blank means
              the same thing on both. */}
          {isDelivery ? (
            <FormField
              name="deliveryFee"
              label={t((m) => m.pos.offPremiseFeeLabel)}
              helperText={
                onPlatform
                  ? // T14 — with a platform, blank means 0: the platform
                    // carries the fee. A typed fee still wins.
                    t((m) => m.pos.platformFeeHint)
                  : defaultDeliveryFee
                    ? t((m) => m.pos.offPremiseFeeHint, {
                        default: String(defaultDeliveryFee),
                      })
                    : t((m) => m.pos.offPremiseFeeHintFree)
              }
              error={
                feeInvalid ? t((m) => m.pos.chargesFeeInvalid) : undefined
              }
            >
              <NumberInput
                name="deliveryFee"
                value={deliveryFeeRaw}
                min={0}
                step={0.01}
                // A fee is money: 12.50 must be typeable (the default is integers).
                allowDecimal
                dir="ltr"
                placeholder={
                  onPlatform
                    ? "0"
                    : defaultDeliveryFee === null
                      ? undefined
                      : String(defaultDeliveryFee)
                }
                onChange={(event) => setFee(event.target.value)}
                data-testid="charges-fee"
              />
            </FormField>
          ) : null}

          <AggregatorChips
            choices={choices}
            value={aggregatorId}
            onChange={pickPlatform}
            disabled={isSubmitting}
            testIdPrefix="charges-aggregator-"
          />
          <PlatformOrderFields
            visible={onPlatform}
            platformOrderRef={platformOrderRef}
            onChange={setPlatformOrderRef}
            disabled={isSubmitting}
            testIdPrefix="charges-"
          />

          {/* T6 round 2 — the chits already on the pass say the OLD channel
              and platform; the cook bags for the wrong door otherwise. */}
          {platesInKitchen ? (
            <p
              role="alert"
              className="rounded-[var(--radius-list)] border border-[color:var(--warning)] px-3 py-2 text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-bold text-[color:var(--warning)]"
              data-testid="charges-reprint-warning"
            >
              {t((m) => m.pos.chargesReprintWarning)}
            </p>
          ) : null}

          {/* WHAT SAVING DOES, on the screen rather than in a surprise on the
              bill: the fee, the service charge and the commission are
              re-priced for the chosen channel and platform. The dish prices
              are not touched, and the sentence says so because a form that
              moves a total invites the question. */}
          <p className="text-[length:var(--font-size-2xs)] font-bold text-[color:var(--text-muted)]">
            {t((m) => m.pos.chargesHint)}
          </p>
        </div>
      </Modal>
      <UnsavedChangesPrompt
        isOpen={guard.asking}
        onDiscard={guard.discard}
        onKeepEditing={guard.keepEditing}
        testId="charges-discard-prompt"
      />
    </>
  );
}
